import { Capacitor, registerPlugin } from '@capacitor/core';
import { getT } from '@/i18n';
import { api } from '@/lib/api';
import { isNativeAndroid, isNativeIos } from '@/lib/platform';
import type { ActivityType } from '@/types';

/**
 * CONNECTOR C2 (`docs/connector/06`, DECISIONS #118): a GRUNDO-aktivitás a
 * telefon saját egészségügyi tárába — iOS: Apple Health (HealthKit), Android:
 * Health Connect. Egy JS-felület, két natív megvalósítás (`HealthStorePlugin`).
 *
 * ⚠️ CSAK A SZERVER ÁLTAL VISSZAIGAZOLT AKTIVITÁS MEHET KI. Az adatot a
 * szerver adja (`/export?format=json`, a `connector` funkciókapun át), nem a
 * helyi rögzítés: így az egészségügyi tárba nem kerülhet olyan edzés, ami a
 * GRUNDO-ban nem létezik.
 *
 * ⚠️ IDEMPOTENS. A natív oldal az aktivitás azonosítójával jelöli a rekordot
 * (HealthKit: `HKMetadataKeySyncIdentifier`, Health Connect: `clientRecordId`),
 * és ha már megvan, nem ír újat — a kézi gomb és az automatikus mentés
 * ugyanarra az aktivitásra sem duplikál.
 *
 * ⚠️ A TELJES ÚTVONAL MEGY KI (Geri, 2026-10-25): a saját tárba a saját adat
 * kerül, a privát zóna erre nem vonatkozik.
 *
 * A beállítás (összekapcsolva, automatikus) ESZKÖZHÖZ KÖTÖTT, `localStorage`
 * — mint az app-zár: a másik telefonodon más egészségügyi tár van.
 */

export type HealthStoreKind = 'appleHealth' | 'healthConnect';

/** `not_installed` / `update_required`: csak Androidon (a Health Connect app). */
export type HealthAvailability = 'available' | 'not_installed' | 'update_required' | 'unsupported';

export interface HealthStoreStatus {
  availability: HealthAvailability;
  /** A felhasználó engedte-e az edzés írását. */
  canWrite: boolean;
}

interface NativeWorkout {
  activityId: string;
  type: ActivityType;
  title: string;
  startMs: number;
  endMs: number;
  distanceM: number;
  points: { lat: number; lng: number; t: number; accuracy?: number; elevation?: number }[];
}

/**
 * `route` csak iOS-en jön: ott az útvonal külön minta, ami az edzés nélkül is
 * elbukhat (`failed`), vagy egy korábban kiírt edzéshez utólag kerül be
 * (`added`). Androidon az útvonal az edzés része, ott nincs külön kimenet.
 */
export interface HealthExportResult {
  written: boolean;
  route?: 'saved' | 'added' | 'none' | 'failed';
  routeError?: string;
}

interface HealthStorePlugin {
  getStatus(): Promise<HealthStoreStatus>;
  requestExport(): Promise<{ canWrite: boolean }>;
  /** `written: false` — már bent volt (idempotens). */
  writeActivity(workout: NativeWorkout): Promise<HealthExportResult>;
  deleteActivity(options: { activityId: string }): Promise<void>;
  /** Android: az engedélyek visszavonása. iOS-en nincs ilyen API — ott no-op. */
  disconnect(): Promise<void>;
  /** Android: a Health Connect telepítése / frissítése / kezelése; iOS: az Egészség app. */
  openStore(): Promise<void>;
}

const HealthStore = registerPlugin<HealthStorePlugin>('HealthStore');

const STORAGE_KEY = 'grundo.healthExport.v1';

interface HealthPrefs {
  enabled: boolean;
  auto: boolean;
}

/** Melyik tár tartozik ehhez a platformhoz; weben nincs. */
export function healthStoreKind(): HealthStoreKind | null {
  if (isNativeIos()) return 'appleHealth';
  if (isNativeAndroid()) return 'healthConnect';
  return null;
}

/** A natív plugin be van-e kötve (egy régebbi app-build még nem ismeri). */
export function healthStoreSupported(): boolean {
  return healthStoreKind() !== null && Capacitor.isPluginAvailable('HealthStore');
}

export function healthPrefs(): HealthPrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<HealthPrefs>) : {};
    return { enabled: parsed.enabled === true, auto: parsed.auto === true };
  } catch {
    return { enabled: false, auto: false };
  }
}

export function setHealthPrefs(patch: Partial<HealthPrefs>): HealthPrefs {
  const next = { ...healthPrefs(), ...patch };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Privát mód: a beállítás csak a munkamenetig él — a kapcsoló így is működik.
  }
  return next;
}

export async function healthStatus(): Promise<HealthStoreStatus> {
  if (!healthStoreSupported()) return { availability: 'unsupported', canWrite: false };
  return HealthStore.getStatus();
}

/** Engedélykérés; siker esetén az összekapcsolás be is kapcsol. */
export async function connectHealthStore(): Promise<boolean> {
  if (!healthStoreSupported()) return false;
  const { canWrite } = await HealthStore.requestExport();
  setHealthPrefs({ enabled: canWrite });
  return canWrite;
}

export async function disconnectHealthStore(): Promise<void> {
  setHealthPrefs({ enabled: false, auto: false });
  if (healthStoreSupported()) await HealthStore.disconnect();
}

export async function openHealthStoreApp(): Promise<void> {
  if (healthStoreSupported()) await HealthStore.openStore();
}

/** A kézi gomb: kiírja az aktivitást. `written: false`, ha már bent volt. */
export async function exportActivityToHealth(activityId: string): Promise<HealthExportResult> {
  const kind = healthStoreKind();
  if (!kind || !healthStoreSupported()) throw new Error(getT().connectedDevices.healthUnavailable);
  const status = await HealthStore.getStatus();
  if (!status.canWrite && !(await connectHealthStore())) {
    throw new Error(getT().connectedDevices.healthDenied[kind]);
  }
  const data = await api.activityHealthExport(activityId);
  const points = data.points
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng) && Number.isFinite(point.t))
    .sort((a, b) => a.t - b.t);
  const result = await HealthStore.writeActivity({
    activityId,
    type: data.type,
    title: getT().connectedDevices.workoutTitle(data.type),
    startMs: data.startedAt,
    endMs: data.endedAt,
    distanceM: data.distanceM,
    points,
  });
  if (result.route === 'failed' || result.route === 'none') {
    console.warn('[healthStore] route not saved', result.route, result.routeError);
  }
  return result;
}

/**
 * Mentés után, ha a felhasználó automatikusra állította. Hibát nem dob: a
 * GRUNDO-mentés már sikerült, az egészségügyi tár csak kiegészítés.
 */
export function autoExportActivityToHealth(activityId: string): void {
  const prefs = healthPrefs();
  if (!prefs.enabled || !prefs.auto || !healthStoreSupported()) return;
  void HealthStore.getStatus()
    .then((status) => (status.canWrite ? exportActivityToHealth(activityId) : null))
    .catch((error: unknown) => console.warn('[healthStore] auto export failed', error));
}

/** A GRUNDO-ban törölt aktivitás az egészségügyi tárból is kikerül (ha mi írtuk be). */
export function deleteActivityFromHealth(activityId: string): void {
  if (!healthPrefs().enabled || !healthStoreSupported()) return;
  void HealthStore.deleteActivity({ activityId }).catch((error: unknown) =>
    console.warn('[healthStore] delete failed', error),
  );
}
